import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Award } from "lucide-react";
import { Hero } from "@/components/Hero";
import { Reveal } from "@/components/Reveal";
import { ListingCard } from "@/components/ListingCard";
import { Marquee } from "@/components/Marquee";
import { SpeakWithMike } from "@/components/SpeakWithMike";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";

const stats = [
  { value: "2009", label: "Licensed Since" },
  { value: "500+", label: "Transactions Closed" },
  { value: "Hall of Fame", label: "& Chairman's Award Honouree" },
  { value: "RE/MAX", label: "Performance Realty" },
];

const trustAwards = ["RE/MAX Platinum Club", "RE/MAX Hall of Fame", "RE/MAX Chairman's Award"];

const chapters = [
  {
    n: "I",
    kicker: "The Practice",
    title: "A steady hand for the region's most complex sales.",
    body: "Foreclosure and court-ordered transactions demand more than a listing — they demand a realtor who understands the legal process, the timelines, and the stakes for everyone at the table.",
  },
  {
    n: "II",
    kicker: "The Standard",
    title: "Precise. Disclosed. Defensible.",
    body: "Distressed assets are handled the way institutional capital expects: nothing hidden, nothing improvised. Every step of the file survives cross-examination.",
  },
  {
    n: "III",
    kicker: "The Region",
    title: "Surrey, Coquitlam, Burnaby — and the corridors between.",
    body: "Fifteen years walking the same streets, tracking the same municipal plans, watching the same skylines rise. The Lower Mainland is the whole practice.",
  },
  {
    n: "IV",
    kicker: "The Trust",
    title: "For developers, lenders, lawyers — and the family across the table.",
    body: "The audience is unusually varied and unusually demanding. Every party leaves the transaction with the sense that it was handled correctly.",
  },
];

export default function Home() {
  const [listings, setListings] = useState([]);

  useEffect(() => {
    api.get("/listings", { params: { property_type: "featured" } })
      .then((r) => setListings(r.data))
      .catch(() => {});
  }, []);

  const curated = listings.slice(0, 3);

  return (
    <div data-testid="home-page">
      <Seo
        title="Mike Sidhu, Personal Real Estate Corporation | Court-Ordered & Foreclosure Real Estate in BC"
        description="Mike Sidhu, Personal Real Estate Corporation with RE/MAX Performance Realty — British Columbia's trusted authority in foreclosure and court-ordered sales. Serving Surrey, Coquitlam, Burnaby & the Lower Mainland since 2009."
        path="/"
      />
      <Hero />

      {/* Stats bar */}
      <section className="bg-navy">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
          {stats.map((s) => (
            <div key={s.label} className="py-10 px-4 text-center">
              <p className="font-heading text-2xl md:text-4xl text-brass leading-tight">{s.value}</p>
              <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-white/60 mt-2">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="border-t border-white/10" data-testid="home-awards-strip">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col sm:flex-row items-center justify-center gap-x-8 gap-y-3">
            {trustAwards.map((a) => (
              <span key={a} className="flex items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.18em] text-white/70">
                <Award size={15} className="text-brass" /> {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial marquee */}
      <Marquee items={["Court-Ordered", "Foreclosure", "Surrey", "Coquitlam", "Burnaby", "Since 2009"]} />

      {/* Manifesto Chapters */}
      <section className="py-28 md:py-40" data-testid="manifesto">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="overline">The Manifesto</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-light text-navy mt-6 leading-[1.05] max-w-4xl">
              Four principles<br />
              <span className="italic text-brass">of practice.</span>
            </h2>
          </Reveal>

          <div className="mt-20 md:mt-28 space-y-20 md:space-y-28">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={0.05}>
                <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-start border-t border-navy/10 pt-10 md:pt-14">
                  <div className="lg:col-span-3 flex lg:flex-col items-baseline gap-4 lg:gap-6">
                    <motion.p
                      initial={{ opacity: 0, x: -14 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.05 * i }}
                      className="font-heading text-6xl md:text-7xl text-brass leading-none"
                    >
                      {c.n}
                    </motion.p>
                    <span className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-ink-soft">
                      Chapter {c.n} · {c.kicker}
                    </span>
                  </div>
                  <div className="lg:col-span-9">
                    <h3 className="font-heading text-2xl md:text-4xl font-light text-navy leading-[1.15] max-w-3xl">
                      {c.title}
                    </h3>
                    <p className="font-body text-ink-soft text-lg leading-relaxed mt-6 max-w-2xl">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Featured listings preview — curated 3 */}
      <section className="bg-white py-24 md:py-32 border-y border-navy/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <Reveal><p className="overline">Current File</p></Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-heading text-3xl md:text-5xl font-light text-navy mt-3 leading-tight">
                  Featured Listings
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <Link
                to="/listings"
                data-testid="home-view-all-listings"
                className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-navy hover:text-brass transition-colors border-b border-brass pb-1"
              >
                View all listings <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {curated.map((l, i) => (
              <Reveal key={l.id} delay={i * 0.1}><ListingCard listing={l} index={i} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Speak With Mike (functional form) */}
      <SpeakWithMike />

      {/* Book CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal><p className="overline">Or Schedule Directly</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-heading text-3xl md:text-5xl font-light text-navy mt-4 leading-tight">
              Pick a time that <span className="italic text-brass">works for you.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/book"
              data-testid="home-cta-book"
              className="group inline-flex items-center gap-2 mt-10 bg-navy text-white px-8 py-4 hover:bg-brass transition-colors duration-300 font-body text-sm tracking-wide"
            >
              Book a Consultation
              <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
